import React, { useState } from "react";
import { toast } from "sonner";
import { Play, Loader2, CheckCircle2, XCircle, Sparkles, Lightbulb } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

function SqlTaskCard({ caseStudyId, task, initialResult, onDone, apiPrefix }) {
  const [query, setQuery] = useState(initialResult?.query || "");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(initialResult || null);

  const run = async () => {
    if (!query.trim()) {
      toast.error("Write a SQL query first");
      return;
    }
    setRunning(true);
    try {
      const { data } = await api.post(`${apiPrefix}/${caseStudyId}/sql/${task.id}/submit`, { query });
      setResult(data);
      if (data.success) onDone(task.id, data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to run query");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="glass-panel rounded-2xl p-5" data-testid={`cs-sql-task-${task.id}`}>
      <p className="text-sm text-white leading-relaxed" data-testid={`cs-sql-task-prompt-${task.id}`}>{task.prompt}</p>
      <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1.5"><Lightbulb className="w-3.5 h-3.5 shrink-0" /> {task.hint}</p>

      <textarea
        value={query} onChange={(e) => setQuery(e.target.value)} rows={3} placeholder="SELECT ..."
        data-testid={`cs-sql-task-input-${task.id}`}
        className="w-full mt-3 bg-black/30 border border-white/10 rounded-lg p-3 text-sm font-mono text-indigo-100 placeholder:text-slate-400 focus:outline-none focus:border-indigo-400/40 resize-none"
      />

      <div className="flex items-center justify-between mt-3">
        <button
          onClick={run} disabled={running} data-testid={`cs-sql-task-run-btn-${task.id}`}
          className="flex items-center gap-1.5 text-xs font-semibold bg-primary hover:bg-primary/90 disabled:opacity-60 text-white px-3.5 py-2 rounded-lg"
        >
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Run Query
        </button>
        {result && result.success !== false && (
          <span className={`flex items-center gap-1 text-xs font-bold ${result.is_correct ? "text-emerald-400" : "text-amber-400"}`} data-testid={`cs-sql-task-status-${task.id}`}>
            {result.is_correct ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />} Score: {result.score}/100
          </span>
        )}
      </div>

      {result && result.success === false && (
        <div className="mt-3 rounded-lg p-3 border border-red-400/30 bg-red-400/5" data-testid={`cs-sql-task-error-${task.id}`}>
          <p className="text-xs text-red-300 font-semibold mb-1">{result.error.message}</p>
          <p className="text-xs text-slate-400">{result.error.ai_explanation}</p>
        </div>
      )}

      {result && result.success !== false && (
        <>
          {result.rows?.length > 0 && (
            <div className="mt-3 overflow-x-auto rounded-lg border border-white/5" data-testid={`cs-sql-task-results-${task.id}`}>
              <table className="w-full text-xs">
                <thead><tr className="bg-white/5">{result.columns.map((c) => <th key={c} className="text-left px-3 py-2 text-slate-400 font-semibold">{c}</th>)}</tr></thead>
                <tbody>
                  {result.rows.slice(0, 10).map((row, i) => (
                    <tr key={i} className="border-t border-white/5">{row.map((v, j) => <td key={j} className="px-3 py-1.5 text-slate-300">{String(v)}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-3 rounded-lg p-3 border border-indigo-400/20 bg-indigo-400/5 flex items-start gap-2" data-testid={`cs-sql-task-feedback-${task.id}`}>
            <Sparkles className="w-3.5 h-3.5 text-indigo-400 mt-0.5 shrink-0" />
            <p className="text-xs text-slate-300 leading-relaxed">{result.ai_feedback}</p>
          </div>
        </>
      )}
    </div>
  );
}

export default function CaseStudySqlTasksTab({ caseStudyId, tasks, initialResults, onAllDone, apiPrefix = "/case-studies" }) {
  const [done, setDone] = useState(initialResults || {});

  const handleDone = (taskId, data) => {
    const next = { ...done, [taskId]: data };
    setDone(next);
    if (tasks.every((t) => next[t.id])) onAllDone(true);
  };

  return (
    <div className="space-y-4" data-testid="case-study-sql-tab">
      {tasks.map((t) => (
        <SqlTaskCard key={t.id} caseStudyId={caseStudyId} task={t} initialResult={done[t.id]} onDone={handleDone} apiPrefix={apiPrefix} />
      ))}
    </div>
  );
}
